import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectNotificationEmails,
  tryGetBatchProcessHeader,
  tryPutNotificationEmails,
  selectPreLiquidationHeader,
} from "../../store/slices/backoffice-teachers";
import {
  Box,
  Chip,
  Grid,
  Grow,
  Alert,
  Paper,
  Button,
  Dialog,
  Popper,
  Tooltip,
  MenuItem,
  MenuList,
  Typography,
  AlertTitle,
  DialogTitle,
  DialogActions,
  IconButton,
  useMediaQuery,
  ClickAwayListener,
} from "@mui/material";
import { MoreHoriz, AddCard } from "@mui/icons-material";
import { useNavigate } from "react-router";
import CustomColumnMenu from "../layout/grid-column-menu";
import { DataGrid } from "@mui/x-data-grid";
import NewSettlementDrawer from "./drawer";
import ModalMessages from "../layout/modal-messages";
import Spinner from "../layout/spinner";
import SettlementsSkeleton from "./skeleton";
import moment from "moment";

const BackofficeSettlements = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //TRAE EL ESTADO DE LAS PRELIQUIDACIONES
  const preliquidations = useSelector(selectPreLiquidationHeader);

  //MANEJO DE LAS COLUMNAS
  const [rows, setRows] = useState([]);

  //MANEJO DEL DRAWER
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  //MANEJO APROBACIÓN
  const [finalLiquidationDialog, setFinalLiquidationDialog] = useState(false);
  const [processId, setProcessId] = useState(null);
  const notificationEmails = useSelector(selectNotificationEmails);

  const isMobile = useMediaQuery("(max-width:600px)");

  const fetchData = async () => {
    const action = await dispatch(tryGetBatchProcessHeader());

    if (action.payload.data.isValid) {
      setRows(
        action.payload.data.data.map((x) => {
          return {
            processId: x.processId,
            processType: x.processType,
            dateFrom: x.dateFrom,
            dateTo: x.dateTo,
            userPreliquidation: x.userPreliquidation,
            userAproval: x.userAproval,
            statusProc: x.statusProc,
            statusProcId: x.statusProcId,
            startDate: x.startDate,
            message: x.message,
          };
        })
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    !openConfirmationDialog && fetchData();
  }, [openConfirmationDialog]);

  const approvePreliquidation = async (batchprocessid) => {
    const actionType = 3;
    const action = await dispatch(
      tryPutNotificationEmails({ batchprocessid, actionType })
    );

    if (action.payload.data.isValid) setOpenConfirmationDialog(true);
  };

  //ACCIONES Y COLUMNAS
  const initializeDataGrid = () => {
    return {
      columns: {
        columnVisibilityModel: {
          statusProcId: false,
          message: !isMobile,
          dateFrom: !isMobile,
          dateTo: !isMobile,
          userAproval: !isMobile,
          processType: !isMobile,
          statusProc: !isMobile,
          startDate: !isMobile,
          userPreliquidation: !isMobile,
          actions: !isMobile, // Ocultamos acciones nativas en mobile
        },
      },
      pagination: { paginationModel: { pageSize: 10 } },
    };
  };

  //BOTON DE ACCIONES
  const ActionsRow = ({ item }) => {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);

    const handleMenuItemClick = (event, index) => {
      //ADMINISTRAR PRELIQUIDACION
      if (index === 0) {
        const queryParams = new URLSearchParams({
          batchprocessid: item.row.processId,
          isEditable: true,
        }).toString();
        navigate(`/backoffice/AdminSettlements/?${queryParams}`);
      }

      //APROBAR PRELIQUIDACION
      if (index === 1) {
        setProcessId(item.row.processId);
        setFinalLiquidationDialog(true);
      }

      //VER PRELIQUIDACION
      if (index === 2) {
        const queryParams = new URLSearchParams({
          batchprocessid: item.row.processId,
          isEditable: false,
        }).toString();
        navigate(`/backoffice/AdminSettlements/?${queryParams}`);
      }

      setOpen(false);
    };

    const handleToggle = () => {
      setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
      if (anchorRef.current && anchorRef.current.contains(event.target)) {
        return;
      }
      setOpen(false);
    };

    return (
      <>
        <IconButton
          size="small"
          ref={anchorRef}
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="Acciones"
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{
            padding: "4px",
            backgroundColor: "#225275",
            color: "white",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "#173b54",
            },
          }}
        >
          <MoreHoriz />
        </IconButton>

        <Popper
          sx={{ zIndex: 10 }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          placement="left-start"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper elevation={6}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu">
                    {(item.row.statusProcId == 3 || item.row.statusProcId == 4) && (
                      <MenuItem onClick={(event) => handleMenuItemClick(event, 0)}>
                        Administrar
                      </MenuItem>
                    )}
                    {item.row.statusProcId == 4 && (
                      <MenuItem onClick={(event) => handleMenuItemClick(event, 1)}>
                        Aprobar
                      </MenuItem>
                    )}
                    {(item.row.statusProcId == 5 || item.row.statusProcId == 1) && (
                      <MenuItem onClick={(event) => handleMenuItemClick(event, 2)}>
                        Ver
                      </MenuItem>
                    )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    );
  };

  const chipColorsState = {
    2: "error",
    3: "primary",
    4: "warning",
    5: "success",
  };

  const columns = [
    {
      field: "processId",
      headerName: isMobile ? "Detalle de Liquidación" : "Liquidación",
      headerAlign: isMobile ? "center" : "left",
      align: "left",
      headerClassName: "headRow",
      flex: isMobile ? 1 : 2,
      filterable: false,
      renderCell: (params) => {
        if (params.value === null) return "";
        if (!isMobile) return params.row.processId;

        const chipColor = chipColorsState[params.row.statusProcId] || "default";

        let startDate = params.row.startDate ? moment(params.row.startDate).format("DD/MM/YYYY") : "";
        let dateFrom = params.row.dateFrom ? moment(params.row.dateFrom).format("DD/MM/YYYY") : "";
        let dateTo = params.row.dateTo ? moment(params.row.dateTo).format("DD/MM/YYYY") : "";

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
              width: "100%",
              position: "relative",
              py: 1.5,
              pr: 2,
            }}
          >
            <Box sx={{ position: "absolute", right: -8, top: 4 }}>
              <ActionsRow item={params} />
            </Box>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                mb: 2,
                width: "100%",
              }}
            >
              Liquidación #{params.row.processId}
            </Typography>

            <Typography variant="caption" color="textSecondary" display="block" sx={{ width: "100%" }}>
              {params.row.processType} • {params.row.message}
            </Typography>

            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1, width: "100%" }}>
              Período: {dateFrom} al {dateTo}
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-start", width: "100%" }}>
              <Chip label={params.row.statusProc} color={chipColor} size="small" />
              <Chip label={`Creada: ${startDate}`} size="small" variant="outlined" />
            </Box>
          </Box>
        );
      },
    },
    {
      field: "statusProcId",
      headerName: "statusProcId",
      headerAlign: "center",
      headerClassName: "headRow",
      flex: 1,
      align: "center",
      filterable: false,
    },
    {
      field: "message",
      headerName: "Descripción",
      headerAlign: "center",
      align: "center",
      headerClassName: "headRow",
      flex: 2,
      filterable: false,
    },
    {
      field: "processType",
      headerName: "Tipo",
      headerAlign: "center",
      align: "center",
      headerClassName: "headRow",
      flex: 1,
      filterable: false,
    },
    {
      field: "startDate",
      headerName: "Fecha Creación",
      headerAlign: "center",
      align: "center",
      headerClassName: "headRow",
      flex: 1,
      filterable: false,
      renderCell: (params) => {
        if (params.value === null) return "";
        let startDate = moment(params.value).format("DD/MM/YYYY");
        return (
          <Tooltip title={startDate} arrow>
            <Chip label={startDate} size="small" />
          </Tooltip>
        );
      },
    },
    {
      field: "dateFrom",
      headerName: "Fecha desde",
      headerAlign: "center",
      align: "center",
      headerClassName: "headRow",
      flex: 1,
      filterable: false,
      valueFormatter: (params) => {
        if (params.value === null) return "";
        return moment(params.value).format("DD/MM/YYYY");
      },
    },
    {
      field: "dateTo",
      headerName: "Fecha hasta",
      headerAlign: "center",
      align: "center",
      flex: 1,
      filterable: false,
      headerClassName: "headRow",
      valueFormatter: (params) => {
        if (params.value === null) return "";
        return moment(params.value).format("DD/MM/YYYY");
      },
    },
    {
      field: "userPreliquidation",
      headerName: "Usuario Pre.",
      headerAlign: "center",
      align: "center",
      flex: 1.5,
      filterable: false,
      headerClassName: "headRow",
    },
    {
      field: "userAproval",
      headerName: "Usuario Liq.",
      headerAlign: "center",
      align: "center",
      flex: 1.5,
      filterable: false,
      headerClassName: "headRow",
    },
    {
      field: "statusProc",
      headerName: "Estado",
      headerAlign: "center",
      align: "center",
      flex: 1,
      filterable: false,
      headerClassName: "headRow",
      renderCell: (params) => {
        if (params.value === null) return "";
        const chipColor = chipColorsState[params.row.statusProcId] || "default";
        return (
          <Tooltip title={params.row.statusProc} arrow>
            <Chip label={params.value} color={chipColor} size="small" />
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerAlign: "center",
      align: "center",
      headerName: "",
      width: 60,
      minWidth: 60,
      headerClassName: "headRow",
      filterable: false,
      renderCell: (params) =>
        notificationEmails.isLoading ? (
          <Spinner color="primary" />
        ) : (
          <ActionsRow item={params} />
        ),
    },
  ];

  return (
    <>
      {preliquidations.isLoading ? (
        <SettlementsSkeleton />
      ) : (
        <>
          <Grid container spacing={3} sx={{ pt: 3, pb: 3, minWidth: 0 }}>
            <Grid item xs={12} lg={6}>
              <Typography align="left" style={{ fontWeight: "bold", fontSize: 18 }} variant="h6" component="h1">
                Liquidaciones
              </Typography>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "flex-end",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  fullWidth={isMobile}
                  disabled={preliquidations.isLoading}
                  endIcon={<AddCard />}
                  onClick={() => setOpenDrawer(true)}
                >
                  {preliquidations.isLoading && <Spinner color="primary" />}
                  GENERAR PRELIQUIDACIÓN
                </Button>
              </Box>

              {isMobile && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, flexWrap: "wrap" }}>
                  <Typography align="left" style={{ fontWeight: "bold", fontSize: 14 }}>
                    ESTADO LIQUIDACIONES:
                  </Typography>
                  <Chip label="LIQUIDADO" color="success" size="small" />
                  <Chip label="PRELIQUIDADO" color="primary" size="small" />
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <DataGrid
                slots={{ columnMenu: CustomColumnMenu }}
                autoHeight={true}
                rowSelection={false}
                getRowId={(row) => row.processId}
                disableRowSelectionOnClick
                rows={rows}
                columns={columns}
                pageSizeOptions={[5, 10, 25, 50]}
                initialState={initializeDataGrid()}
                getRowHeight={() => (isMobile ? "auto" : undefined)}
                getEstimatedRowHeight={() => 140}
                sx={{
                  "& .MuiDataGrid-virtualScroller": {
                    minHeight: 250,
                  },
                  "& .MuiTablePagination-spacer": {
                    display: isMobile ? "none" : "block",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    display: isMobile ? "none" : "block",
                  },
                  "& .MuiTablePagination-select": {
                    display: isMobile ? "none" : "flex",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    justifyContent: isMobile ? "center" : "flex-end",
                  },
                  "& .MuiTablePagination-toolbar": {
                    justifyContent: isMobile ? "center" : "flex-end",
                    width: isMobile ? "100%" : "auto",
                    paddingLeft: isMobile ? 0 : 2,
                  },
                }}
              />
            </Grid>
          </Grid>

          {openDrawer && (
            <NewSettlementDrawer
              openDrawer={openDrawer}
              setOpenDrawer={setOpenDrawer}
              setOpenConfirmationDialog={setOpenConfirmationDialog}
            />
          )}

          {openConfirmationDialog && (
            <Dialog
              open={openConfirmationDialog}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {"La información ha sido guardada con éxito"}
              </DialogTitle>
              <DialogActions>
                <Button onClick={() => setOpenConfirmationDialog(false)} autoFocus>
                  Aceptar
                </Button>
              </DialogActions>
            </Dialog>
          )}

          {finalLiquidationDialog && (
            <Dialog
              open={finalLiquidationDialog}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                <Alert severity="warning" sx={{ textAlign: "left" }}>
                  <AlertTitle>¿Confirmas aprobar la preliquidación?</AlertTitle>
                  Este proceso es irreversible, generará la liquidación final
                  para todos los profesores y enviará un correo electrónico a
                  los mismos notificándolos.
                </Alert>
              </DialogTitle>
              <DialogActions>
                <Button
                  onClick={() => {
                    approvePreliquidation(processId);
                    setFinalLiquidationDialog(false);
                  }}
                  autoFocus
                >
                  Aceptar
                </Button>
                <Button
                  onClick={() => {
                    setFinalLiquidationDialog(false);
                  }}
                  autoFocus
                >
                  Cancelar
                </Button>
              </DialogActions>
            </Dialog>
          )}

          {preliquidations.errors && preliquidations.errors.length > 0 && (
            <ModalMessages errors={preliquidations.errors} />
          )}
          {notificationEmails.errors && notificationEmails.errors.length > 0 && (
            <ModalMessages errors={notificationEmails.errors} />
          )}
        </>
      )}
    </>
  );
};

export default BackofficeSettlements;